import React from 'react';
import { Controller } from 'react-hook-form';
import { FaX } from 'react-icons/fa6';
import InputMask from 'react-input-mask';
import { Checkbox, Icon, Input, InputGroup, InputRightAddon, Switch, Textarea } from '@chakra-ui/react';

import dataTypes from '../../../constants/dataTypes';
import colors from '../../../styles/theme/foundation/colors';
import DatePicker from '../../DatePicker';

export const inputTypes = {
  standard: 'standard',
  checkBoxLabelInput: 'checkBoxLabelInput',
  textArea: 'textArea',
  textAreaSm: 'textAreaSm',
  password: 'password',
  phoneNumber: 'phoneNumber',
  monetary: 'monetary',
  checkBox: 'checkBox',
  date: 'date',
  switch: 'switch'
};

export const dataTypeToInputType = {
  [dataTypes.String]: inputTypes.standard,
  [dataTypes.Monetary]: inputTypes.monetary,
  [dataTypes.Boolean]: inputTypes.switch,
  [dataTypes.Date]: inputTypes.date,
  [dataTypes.DateTime]: inputTypes.date
};

const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0 // (causes 2500.99 to be printed as $2,501)
});

export const currencyMask = ({ nextState: { value, ...rest } }) => {
  const number = value.replace(/[\D]/g, '');
  const newValue = value ? formatter.format(number) : value;

  return {
    ...rest,
    value: newValue,
    selection: {
      start: newValue.length,
      end: newValue.length
    }
  };
};

const inputTypeStyles = {
  [inputTypes.checkBox]: {
    component: ({ size, placeholder, register, inputName, validationRules, isDisabled, isReadOnly, dataAnalyticsId, duskSelector, cypressId }) => (
      <Checkbox
        data-analytics-id={dataAnalyticsId}
        data-cy={cypressId}
        dusk={duskSelector ?? dataAnalyticsId}
        isDisabled={isDisabled}
        isReadOnly={isReadOnly}
        placeholder={placeholder}
        size={size}
        type="checkbox"
        variant="standard"
        {...register(inputName, validationRules)}
      />
    ),
    styles: { justifyContent: 'space-around' }
  },
  [inputTypes.switch]: {
    component: ({ size, placeholder, register, inputName, validationRules, isDisabled, isReadOnly, dataAnalyticsId, duskSelector, cypressId }) => (
      <Switch
        data-analytics-id={dataAnalyticsId}
        data-cy={cypressId}
        dusk={duskSelector ?? dataAnalyticsId}
        id={`${inputName}-${inputTypes.switch}`}
        name={inputName}
        {...register(inputName)}
        isDisabled={isDisabled}
        isReadOnly={isReadOnly}
        size={size}
      />
    )
  },

  [inputTypes.date]: {
    component: ({ size, placeholder, register, inputName, validationRules, isDisabled, control, isReadOnly, error, minDate, maxDate, dataAnalyticsId, duskSelector, cypressId }) => (
      <Controller
        control={control}
        name={inputName}
        render={({ field }) => (
          <DatePicker
            data-cy={cypressId}
            dateFormat="MM/DD/YYYY"
            dusk={duskSelector ?? dataAnalyticsId}
            error={error}
            existingDate={field?.value || ''}
            isDisabled={isDisabled}
            maxDate={maxDate}
            minDate={minDate}
            placeholderText={placeholder}
            width="100%"
            onDateSelect={date => field.onChange(date)}
          />
        )}
        rules={validationRules}
      />
    ),
    styles: { justifyContent: 'space-around' }
  },
  [inputTypes.standard]: {
    component: ({ size, placeholder, register, inputName, validationRules, isDisabled, isReadOnly, disableEnterKey, dataAnalyticsId, duskSelector, cypressId }) => (
      <Input
        _disabled={{ color: 'greyScale.600' }}
        data-analytics-id={dataAnalyticsId}
        data-cy={cypressId}
        dusk={duskSelector ?? dataAnalyticsId}
        isDisabled={isDisabled}
        isReadOnly={isReadOnly}
        pl="16px"
        placeholder={placeholder}
        size={size}
        variant="standard"
        onKeyDown={evt => {
          if (disableEnterKey && evt.keyCode === 13) evt.preventDefault();
        }}
        {...register(inputName, validationRules)}
      />
    ),
    styles: { justifyContent: 'space-around' }
  },
  [inputTypes.checkBoxLabelInput]: {
    component: ({ size, placeholder, register, inputName, validationRules, isDisabled, isReadOnly, error, dataAnalyticsId, duskSelector, cypressId }) => {
      const borderColor = error ? colors.error.primary['600'] : colors.primaryScale['600'];
      return (
        <Input
          _autofill={{ boxShadow: '0 0 0px 1000px white inset' }}
          _focus={{
            outline: 'none',
            bgColor: 'white',
            boxShadow: '0 0 0px 1000px white inset',
            borderBottom: `2px solid ${borderColor}`
          }}
          borderBottom={`1px solid ${borderColor}`}
          borderRadius="0px"
          color={colors.greyScale['900']}
          data-cy={cypressId}
          dusk={duskSelector ?? dataAnalyticsId}
          isDisabled={isDisabled}
          isReadOnly={isReadOnly}
          pl="16px"
          placeholder={placeholder}
          size={size}
          variant="unstyled"
          {...register(inputName, validationRules)}
          p={2}
        />
      );
    },
    styles: {
      justifyContent: '',
      color: colors.greyScale['900'],
      bg: 'transparent',
      height: '40px',
      mt: '20px',
      inputBoxStyles: {
        border: 'none',
        boxShadow: 'none'
      }
    }
  },
  [inputTypes.monetary]: {
    component: ({ size, placeholder, inputName, isDisabled, isReadOnly, control,
      disableEnterKey, dataAnalyticsId, duskSelector, cypressId, setValue }) => (
        <Controller
          as={InputMask}
          control={control}
          name={inputName}
          render={({ field: { onChange, value } }) => {
            if (value && typeof value === 'number') {
              value = `$${value}`;
            }

            return (
              <InputGroup>
                <InputMask
                  alwaysShowMask={false}
                  beforeMaskedStateChange={currencyMask}
                  mask="$999999999999999"
                  value={value}
                  onChange={onChange}
                >
                  <Input
                    data-analytics-id={dataAnalyticsId}
                    data-cy={cypressId}
                    defaultValue={value}
                    dusk={duskSelector ?? dataAnalyticsId}
                    isDisabled={isDisabled}
                    isReadOnly={isReadOnly}
                    pl="16px"
                    placeholder={placeholder}
                    size={size}
                    variant="standard"
                    onChange={onChange}
                    onKeyDown={evt => {
                      if (disableEnterKey && evt.keyCode === 13) evt.preventDefault();
                    }}
                  />
                </InputMask>
                {value && (
                  <InputRightAddon
                    cursor="pointer"
                    onClick={() => setValue(inputName, '')}
                  >
                    <Icon as={FaX} />
                  </InputRightAddon>
                )}
              </InputGroup>
            );
          }}
        />
    ),
    styles: { justifyContent: 'space-around' }
  },
  [inputTypes.phoneNumber]: {
    component: ({ size,
      placeholder, inputName, isDisabled, isReadOnly, control, disableEnterKey, dataAnalyticsId,
      validationRules, duskSelector, cypressId }) => (
        <Controller
          as={InputMask}
          control={control}
          name={inputName}
          render={({ field: { onChange, value } }) => (
            <InputMask mask="(999) 999-9999" value={value} onChange={onChange}>
              <Input
                data-analytics-id={dataAnalyticsId}
                data-cy={cypressId}
                defaultValue={value}
                dusk={duskSelector ?? dataAnalyticsId}
                isDisabled={isDisabled}
                isReadOnly={isReadOnly}
                name={inputName}
                pl="16px"
                placeholder={placeholder}
                size={size}
                variant="standard"
                onChange={onChange}
                onKeyDown={evt => {
                  if (disableEnterKey && evt.keyCode === 13) evt.preventDefault();
                }}
              />
            </InputMask>
          )}
          rules={validationRules}
        />
    ),
    styles: { justifyContent: 'space-around' }
  },
  [inputTypes.textArea]:
    {
      component: ({ placeholder, register, inputName, validationRules, isDisabled, isReadOnly, dataAnalyticsId, duskSelector, cypressId }) => (
        <Textarea
          _disabled={{ color: 'greyScale.600' }}
          _hover={{ cursor: [isDisabled ? 'not-allowed' : 'auto'] }}
          _placeHolder={{ color: 'greyScale.500' }}
          border="0px"
          color={isDisabled ? 'greyScale.500' : 'greyScale.900'}
          data-analytics-id={dataAnalyticsId}
          data-cy={cypressId}
          dusk={duskSelector ?? dataAnalyticsId}
          fontSize="sm"
          height="inherit"
          isDisabled={isDisabled}
          isReadOnly={isReadOnly}
          pl="16px"
          placeholder={placeholder}
          pr="8px"
          pt={3}
          resize="none"
          variant="unstyled"
          {...register(inputName, validationRules)}
        />
      ),
      styles:
        {
          height: '135px',
          justifyContent:
            'flex-start'
        }
    },
  [inputTypes.textAreaSm]:
    {
      component: ({ placeholder, register, inputName, validationRules, isDisabled, isReadOnly, dataAnalyticsId, duskSelector, cypressId }) => (
        <Textarea
          _hover={{ cursor: [isDisabled ? 'not-allowed' : 'auto'] }}
          _placeHolder={{ color: 'greyScale.500' }}
          border="0px"
          color={isDisabled ? 'greyScale.500' : 'greyScale.900'}
          data-analytics-id={dataAnalyticsId}
          data-cy={cypressId}
          dusk={duskSelector ?? dataAnalyticsId}
          fontSize="sm"
          height="inherit"
          isDisabled={isDisabled}
          isReadOnly={isReadOnly}
          overflow="hidden"
          pl="16px"
          placeholder={placeholder}
          pr="8px"
          pt={2}
          resize="none"
          variant="unstyled"
          {...register(inputName, validationRules)}
        />
      ),
      styles:
        {
          height: '80px',
          justifyContent:
            'flex-start'
        }
    },
  [inputTypes.password]:
    {
      component: ({ size, placeholder, register, inputName, validationRules, isDisabled, disableEnterKey, dataAnalyticsId, duskSelector, cypressId }) => (
        <Input
          data-analytics-id={dataAnalyticsId}
          data-cy={cypressId}
          dusk={duskSelector ?? dataAnalyticsId}
          isDisabled={isDisabled}
          placeholder={placeholder}
          variant="standard"
          {...register(inputName, validationRules)}
          overflow="hidden"
          pl="16px"
          size={size}
          type="password"
          onKeyDown={evt => {
            if (disableEnterKey && evt.keyCode === 13) evt.preventDefault();
          }}
        />
      ),
      styles:
        {
          justifyContent:
            'space-around'
        }
    }
};

export const getInputTypeStyle = (type, dataType) => {
  const mappedType = dataType
    ? dataTypeToInputType[dataType] || dataType
    : type;

  if (!inputTypeStyles[mappedType]) {
    throw new Error(`inputType of ${mappedType} for InputReactHookForm does not exist`);
  }

  return inputTypeStyles[mappedType];
};

export const getInputType = dataType => dataTypeToInputType[dataType] || inputTypes.standard;
